import React from "react";
import { Link } from "react-router-dom";
import { FolderOpen, BarChart3, Clock3, Coins, CalendarDays, ChevronRight } from "lucide-react";
import { IconTile } from "@/portal/ui/Layout";
import { fmtDateHuman, fmtTime, pluralize } from "@/portal/format";

const pad2 = (n) => String(n ?? 0).padStart(2, "0");

function Kpi({ icon, label, value, sub, subClass = "", tone, to, testid }) {
  const body = (
    <>
      <IconTile icon={icon} tone={tone} />
      <div className="min-w-0 flex-1">
        <div className="text-[12px] text-cp-text-2 leading-tight">{label}</div>
        <div className="flex items-baseline gap-2.5 mt-1 min-w-0">
          <span className="cp-metric !text-[26px] shrink-0" data-testid={testid ? `${testid}-value` : undefined}>{value}</span>
          {sub && <span className={`max-lg:hidden text-[11px] leading-[1.25] text-cp-text-3 line-clamp-2 ${subClass}`}>{sub}</span>}
        </div>
      </div>
    </>
  );
  const cls = `cp-panel flex items-center gap-3.5 px-4 py-3.5 min-w-0 ${to ? "cp-lift" : ""}`;
  return to ? <Link to={to} className={cls} data-testid={testid}>{body}</Link> : <div className={cls} data-testid={testid}>{body}</div>;
}

function MeetingKpi({ m }) {
  const inner = (
    <>
      <IconTile icon={CalendarDays} />
      <div className="min-w-0 flex-1">
        <div className="text-[12px] text-cp-text-2 leading-tight">Următoarea ședință</div>
        {m ? (
          <>
            <div className="cp-serif text-[20px] leading-tight text-cp-text mt-1 truncate" data-testid="kpi-meeting-value">{fmtDateHuman(m.when)}</div>
            <div className="text-[11.5px] text-cp-text-3 truncate mt-0.5">{fmtTime(m.when)} · {m.project_name}</div>
          </>
        ) : <div className="cp-serif text-[20px] leading-tight text-cp-text mt-1" data-testid="kpi-meeting-value">Neprogramată</div>}
      </div>
      {m && <ChevronRight className="h-4 w-4 text-cp-text-3 shrink-0" aria-hidden="true" />}
    </>
  );
  const cls = "cp-panel flex items-center gap-3.5 px-4 py-3.5 min-w-0 col-span-2 md:col-span-1 cp-lift";
  return m
    ? <Link to={`/portal/proiecte/${m.project_id}?tab=calendar`} className={cls} data-testid="kpi-meeting">{inner}</Link>
    : <div className={cls} data-testid="kpi-meeting">{inner}</div>;
}

export function HomeKpis({ ov, featured }) {
  const s = ov.stats;
  const budgetPid = featured && ov.budgets?.[featured.id] ? featured.id : Object.keys(ov.budgets || {})[0];
  const decisionsSub = s.overdue_decisions
    ? { text: `${pluralize(s.overdue_decisions, "termen depășit", "termene depășite")}`, cls: "!text-cp-danger font-medium" }
    : s.docs_to_review ? { text: `+${pluralize(s.docs_to_review, "document", "documente")} de revizuit`, cls: "!text-cp-warning font-medium" }
      : { text: s.pending_decisions ? "așteaptă răspunsul tău" : "nimic urgent", cls: "" };
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-[1fr_1fr_1fr_1fr_1.35fr] gap-4" data-testid="home-kpis">
      <Kpi icon={FolderOpen} label="Proiecte active" value={pad2(s.active)} sub={`din ${pluralize(s.projects, "proiect", "proiecte")}`} to="/portal/proiecte" testid="kpi-active" />
      <Kpi icon={BarChart3} label="Progres general" value={`${s.progress_avg ?? 0}%`} sub="media proiectelor active" testid="kpi-progress" />
      <Kpi icon={Clock3} tone={s.pending_decisions ? "amber" : ""} label="Decizii în așteptare" value={pad2(s.pending_decisions)} sub={decisionsSub.text} subClass={decisionsSub.cls} to="/portal/decizii" testid="kpi-decisions" />
      <Kpi icon={Coins} label="Buget utilizat" value={s.budget_used_pct != null ? `${s.budget_used_pct}%` : "—"} sub={s.budget_used_pct != null ? "din bugetul aprobat" : "nepublicat încă"}
        to={budgetPid ? `/portal/proiecte/${budgetPid}?tab=buget` : undefined} testid="kpi-budget" />
      <MeetingKpi m={ov.upcoming_meetings?.[0]} />
    </div>
  );
}
